import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { History, Calculator, Dices, ArrowRight, Trophy, Wallet, ListChecks, CalendarClock, Sparkles } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { supabase } from "@/integrations/supabase/client";
import { useLottery, type LotterySlug } from "@/hooks/use-lottery-selection";
import { useAuth } from "@/hooks/use-auth";
import { formatBRL } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { formatSpecialDate, getActiveSpecial, type SpecialDraw } from "@/lib/check-scope";

// Dias da semana em que cada loteria realiza sorteios (0 = domingo).
// A Caixa deixou de sortear aos sábados: o antigo sorteio de sábado 21h
// passou para domingo às 11h.
const DRAW_WEEKDAYS: Record<LotterySlug, number[]> = {
  "mega-sena":  [2, 4, 0],             // ter, qui, dom
  "lotofacil":  [1, 2, 3, 4, 5, 0],    // seg–sex + dom
  "quina":      [1, 2, 3, 4, 5, 0],    // seg–sex + dom
  "dupla-sena": [1, 3, 5],             // seg, qua, sex
};

// Horário do sorteio por dia da semana (BRT). Domingo é 11h; dias úteis, 21h.
const drawCutoffHourBRT = (weekday: number) => (weekday === 0 ? 11 : 21);

// "Hoje" e "agora" em horário de Brasília (UTC-3, sem DST desde 2019).
function brtNow(): { dateISO: string; hour: number; weekday: number } {
  const now = new Date();
  const brt = new Date(now.getTime() - 3 * 60 * 60 * 1000);
  return {
    dateISO: brt.toISOString().slice(0, 10),
    hour: brt.getUTCHours(),
    weekday: brt.getUTCDay(),
  };
}

/**
 * Próxima data de sorteio segundo o calendário fixo da Caixa, considerando
 * o corte de 21h BRT do dia. Usada como fallback quando não há registro em
 * lottery_schedule.
 */
function nextDrawDateFallback(slug: LotterySlug): string | null {
  const days = DRAW_WEEKDAYS[slug] ?? [];
  if (!days.length) return null;
  const { dateISO, hour, weekday } = brtNow();
  // Se hoje é dia de sorteio dessa loteria e ainda não passou do horário,
  // conta hoje como próximo; caso contrário, começa a partir de amanhã.
  const isDrawToday = days.includes(weekday);
  const startOffset = isDrawToday && hour < drawCutoffHourBRT(weekday) ? 0 : 1;
  const base = new Date(dateISO + "T12:00:00Z");
  for (let i = startOffset; i <= 14; i++) {
    const next = new Date(base);
    next.setUTCDate(base.getUTCDate() + i);
    if (days.includes(next.getUTCDay())) {
      return next.toISOString().slice(0, 10);
    }
  }
  return null;
}

const Dashboard = () => {
  const { config } = useLottery();
  const { user } = useAuth();
  const [stats, setStats] = useState<{ count: number; latest: number | null; latestDate: string | null }>({
    count: 0, latest: null, latestDate: null,
  });
  const [me, setMe] = useState<{ games: number; spent: number; prizes: number; bestHits: number | null }>({
    games: 0, spent: 0, prizes: 0, bestHits: null,
  });
  const [recentPrizes, setRecentPrizes] = useState<Array<{ id: string; contest_number: number; hits: number; label: string | null; created_at: string }>>([]);
  const [upcoming, setUpcoming] = useState<{ nextContest: number | null; nextDate: string | null; prize: number | null } | null>(null);

  useEffect(() => {
    (async () => {
      const { data: lottery } = await supabase
        .from("lotteries").select("id").eq("slug", config.slug).single();
      if (!lottery) return;

      const { count } = await supabase
        .from("draws").select("*", { count: "exact", head: true }).eq("lottery_id", lottery.id);

      const { data: latest } = await supabase
        .from("draws").select("contest_number, draw_date")
        .eq("lottery_id", lottery.id)
        .order("contest_number", { ascending: false }).limit(1).single();

      setStats({
        count: count ?? 0,
        latest: latest?.contest_number ?? null,
        latestDate: latest?.draw_date ?? null,
      });
    })();
  }, [config.slug]);

  useEffect(() => {
    if (!user) return;
    (async () => {
      const { data: games } = await supabase
        .from("saved_games")
        .select("id, cost")
        .eq("user_id", user.id);
      const totalGames = games?.length ?? 0;
      const spent = (games ?? []).reduce((s, g) => s + Number(g.cost ?? 0), 0);

      const { data: results } = await supabase
        .from("game_results")
        .select("hits, is_prize")
        .eq("user_id", user.id);
      const prizes = (results ?? []).filter((r) => r.is_prize).length;
      const bestHits = (results ?? []).reduce<number | null>(
        (m, r) => (m === null || r.hits > m ? r.hits : m),
        null
      );

      setMe({ games: totalGames, spent, prizes, bestHits });

      const { data: recent } = await supabase
        .from("game_results")
        .select("id, contest_number, hits, created_at, saved_games(label)")
        .eq("user_id", user.id)
        .eq("is_prize", true)
        .order("created_at", { ascending: false })
        .limit(5);
      setRecentPrizes(
        (recent ?? []).map((r: { id: string; contest_number: number; hits: number; created_at: string; saved_games: { label: string | null } | null }) => ({
          id: r.id,
          contest_number: r.contest_number,
          hits: r.hits,
          label: r.saved_games?.label ?? null,
          created_at: r.created_at,
        }))
      );
    })();
  }, [user]);

  // Próximo concurso da loteria selecionada: usa lottery_schedule quando houver,
  // senão calcula a partir do último concurso + calendário fixo dos sorteios da Caixa.
  useEffect(() => {
    (async () => {
      setUpcoming(null);
      const { data: lottery } = await supabase
        .from("lotteries").select("id").eq("slug", config.slug).maybeSingle();
      if (!lottery) return;
      const { data: latest } = await supabase
        .from("draws")
        .select("contest_number, draw_date")
        .eq("lottery_id", lottery.id)
        .order("contest_number", { ascending: false })
        .limit(1)
        .maybeSingle();
      const { data: sched } = await supabase
        .from("lottery_schedule")
        .select("contest_number, draw_date, estimated_prize")
        .eq("lottery_id", lottery.id)
        .gt("contest_number", latest?.contest_number ?? 0)
        .order("contest_number", { ascending: true })
        .limit(1)
        .maybeSingle();
      // Prêmio estimado do próximo concurso vindo do último resultado importado
      // (a Caixa divulga junto com o resultado). Usamos como fallback quando o
      // lottery_schedule não trouxer estimativa.
      const { data: latestPrize } = await supabase
        .from("draws")
        .select("next_estimated_prize, accumulated_amount")
        .eq("lottery_id", lottery.id)
        .order("contest_number", { ascending: false })
        .limit(1)
        .maybeSingle();
      // Número e data sempre vêm da mesma linha da agenda. Isso evita combinar
      // "último resultado + 1" com a data pertencente a outro concurso.
      const schedRow = sched;
      const nextContest = schedRow?.contest_number ?? (latest?.contest_number ? latest.contest_number + 1 : null);
      const nextDate = schedRow?.draw_date ?? nextDrawDateFallback(config.slug);
      const prizeFromDraw = latestPrize?.next_estimated_prize != null
        ? Number(latestPrize.next_estimated_prize)
        : (latestPrize?.accumulated_amount != null ? Number(latestPrize.accumulated_amount) : null);
      setUpcoming({
        nextContest,
        nextDate,
        prize: schedRow?.estimated_prize != null ? Number(schedRow.estimated_prize) : prizeFromDraw,
      });
    })();
  }, [config.slug]);

  const [special, setSpecial] = useState<SpecialDraw | null>(null);
  useEffect(() => {
    let cancelled = false;
    getActiveSpecial(config.slug).then((s) => { if (!cancelled) setSpecial(s); });
    return () => { cancelled = true; };
  }, [config.slug]);

  const cards = [
    { title: "Histórico de resultados", desc: "Veja todos os concursos com filtros e busca.", to: "/app/historico", icon: History },
    { title: "Probabilidades", desc: "Calcule chances de acerto para cada loteria.", to: "/app/probabilidades", icon: Calculator },
    { title: "Gerador aleatório", desc: "Gere jogos com distribuição equilibrada.", to: "/app/gerador", icon: Dices },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-serif text-3xl text-foreground">Visão geral da sua conta</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Loteria selecionada: <span className="text-foreground">{config.name}</span>
        </p>
      </div>

      {special && (
        <Card className="border-primary/50 bg-primary/5">
          <CardContent className="flex flex-wrap items-center justify-between gap-3 py-4">
            <div className="flex items-start gap-3">
              <Sparkles className="mt-0.5 h-5 w-5 text-primary" />
              <div>
                <div className="font-serif text-lg text-foreground">
                  {special.special_name ?? "Sorteio especial"}
                </div>
                <p className="text-sm text-muted-foreground">
                  Sorteio em {formatSpecialDate(special.draw_date)}
                  {special.contest_number != null ? ` · concurso #${special.contest_number}` : ""}
                  {special.estimated_prize != null ? ` · prêmio estimado ${formatBRL(Number(special.estimated_prize))}` : ""}
                  {" "}— ao salvar seus jogos escolha a opção do sorteio especial.
                </p>
              </div>
            </div>
            <Button asChild size="sm">
              <Link to="/app/gerador">Fazer jogos para este sorteio</Link>
            </Button>
          </CardContent>
        </Card>
      )}

      <h2 className="sr-only">Resumo dos concursos</h2>
      <div className="grid gap-4 sm:grid-cols-3">
        <Card className="border-lt-border bg-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-xs font-normal uppercase tracking-wider text-muted-foreground">Concursos no banco</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="font-serif text-3xl text-foreground">{stats.count.toLocaleString("pt-BR")}</div>
          </CardContent>
        </Card>
        <Card className="border-lt-border bg-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-xs font-normal uppercase tracking-wider text-muted-foreground">Último concurso</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="font-serif text-3xl text-foreground">{stats.latest ?? "—"}</div>
          </CardContent>
        </Card>
        <Card className="border-lt-border bg-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-xs font-normal uppercase tracking-wider text-muted-foreground">Data</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="font-serif text-3xl text-foreground">
              {stats.latestDate ? new Date(stats.latestDate + "T12:00:00").toLocaleDateString("pt-BR") : "—"}
            </div>
          </CardContent>
        </Card>
      </div>

      <Card className="border-lt-border bg-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 font-serif text-lg">
            <CalendarClock className="h-4 w-4 text-primary" /> Próximo concurso — {config.name}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {upcoming ? (
            <div className="grid gap-4 sm:grid-cols-3">
              <div>
                <div className="text-xs uppercase tracking-wider text-muted-foreground">Concurso</div>
                <div className="mt-1 font-serif text-3xl text-foreground">
                  {upcoming.nextContest != null ? `#${upcoming.nextContest}` : "—"}
                </div>
              </div>
              <div>
                <div className="text-xs uppercase tracking-wider text-muted-foreground">Data do sorteio</div>
                <div className="mt-1 font-serif text-3xl text-foreground">
                  {upcoming.nextDate
                    ? new Date(upcoming.nextDate + "T12:00:00").toLocaleDateString("pt-BR", { weekday: "long", day: "2-digit", month: "2-digit" })
                    : "—"}
                </div>
              </div>
              {upcoming.prize != null && (
                <div>
                  <div className="text-xs uppercase tracking-wider text-muted-foreground">Prêmio estimado</div>
                  <div className="mt-1 font-serif text-3xl text-primary">{formatBRL(upcoming.prize)}</div>
                </div>
              )}
            </div>
          ) : (
            <div className="text-sm text-muted-foreground">Carregando…</div>
          )}
        </CardContent>
      </Card>

      <div>
        <h2 className="mb-3 font-serif text-xl text-foreground">Sua atividade</h2>
        <div className="grid gap-4 sm:grid-cols-4">
          <Card className="border-lt-border bg-card">
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-1.5 text-xs font-normal uppercase tracking-wider text-muted-foreground">
                <ListChecks className="h-3 w-3" /> Jogos salvos
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="font-serif text-3xl text-foreground">{me.games}</div>
            </CardContent>
          </Card>
          <Card className="border-lt-border bg-card">
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-1.5 text-xs font-normal uppercase tracking-wider text-muted-foreground">
                <Wallet className="h-3 w-3" /> Total apostado
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="font-serif text-3xl text-foreground">
                {formatBRL(me.spent)}
              </div>
            </CardContent>
          </Card>
          <Card className="border-lt-border bg-card">
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-1.5 text-xs font-normal uppercase tracking-wider text-muted-foreground">
                <Trophy className="h-3 w-3" /> Prêmios
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="font-serif text-3xl text-primary">{me.prizes}</div>
            </CardContent>
          </Card>
          <Card className="border-lt-border bg-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-xs font-normal uppercase tracking-wider text-muted-foreground">Melhor acerto</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="font-serif text-3xl text-foreground">
                {me.bestHits ?? "—"}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {recentPrizes.length > 0 && (
        <Card className="border-primary/40 bg-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 font-serif text-lg">
              <Trophy className="h-4 w-4 text-primary" /> Últimos prêmios
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {recentPrizes.map((p) => (
              <Link
                key={p.id}
                to="/app/jogos"
                className="flex items-center justify-between border-b border-lt-border/50 pb-2 text-sm last:border-0 last:pb-0 hover:text-primary"
              >
                <span>
                  <span className="text-foreground">{p.label || "Jogo sem nome"}</span>
                  <span className="text-muted-foreground"> • Concurso {p.contest_number}</span>
                </span>
                <span className="text-primary">{p.hits} acertos</span>
              </Link>
            ))}
          </CardContent>
        </Card>
      )}

      <div className="grid gap-4 md:grid-cols-3">
        {cards.map((c) => (
          <Link key={c.to} to={c.to}>
            <Card className="group h-full border-lt-border bg-card transition-colors hover:border-primary/50">
              <CardHeader>
                <c.icon className="h-6 w-6 text-primary" />
                <CardTitle className="mt-3 font-serif text-xl">{c.title}</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground">{c.desc}</p>
                <div className="mt-3 flex items-center gap-1 text-xs text-primary opacity-0 transition-opacity group-hover:opacity-100">
                  Abrir <ArrowRight className="h-3 w-3" />
                </div>
              </CardContent>
            </Card>
          </Link>
        ))}
      </div>

      <p className="text-xs text-muted-foreground">
        💡 O histórico completo está sendo importado em background. Os números podem aumentar a cada poucos minutos.
      </p>
    </div>
  );
};

export default Dashboard;
